// Every link we render to a source article carries rel="noopener noreferrer", which strips the
// Referer header — so written help's own analytics cannot tell an AIA-driven visit from organic
// search, and the content team has no way to see which articles we send traffic to. Stamping UTM
// params on the way out is the only signal that survives the new tab.
//
// Scoped to our own written-help domains on purpose: third-party sources (Skatteverket,
// Bolagsverket, …) must leave here byte-identical to what the content index returned.

import { hostMatchesDomain } from './hostMatchesDomain';

/** Which AIA surface the click came from; lands in utm_campaign. */
export type ReferralSurface = 'citation' | 'answer-link' | 'support-link';

// vismaspcs.se is the pre-rebrand written-help host, and it is still the one the content index
// carries for a large part of the Dutch catalogue (`support.vismaspcs.se/<product>/nl-nl/...`) —
// the NL product portal links there too. Those URLs 301 to the matching support.spiris.se page
// *with the query string intact* (verified against the live redirect, including the product-slug
// renames), so a stamped link lands on the same page, in the same tracker, as a spiris.se one.
// Without this entry NL citations were the one internal surface leaving here undecorated.
const INTERNAL_DOMAINS = ['spiris.se', 'vismaspcs.se'];

/** True for our written-help domains and any subdomain of them. */
export function isInternalHostname(hostname: string): boolean {
    return hostMatchesDomain(hostname, INTERNAL_DOMAINS);
}

// Only absolute http(s) URLs are considered: the other branch isSafeUrl allows is a same-origin
// relative path, which is AIA's own app rather than a help article, and rewriting those would also
// turn them absolute.
export function withReferralParams(url: string, surface: ReferralSurface): string {
    if (!/^https?:\/\//i.test(url)) {
        return url;
    }

    let parsed: URL;
    try {
        parsed = new URL(url);
    } catch {
        return url;
    }

    if (!isInternalHostname(parsed.hostname)) {
        return url;
    }

    // set(), not append() — a utm_* value already baked into the indexed URL would otherwise split
    // this article's traffic across two rows in the content team's report.
    parsed.searchParams.set('utm_source', 'ai-assistant');
    parsed.searchParams.set('utm_medium', 'referral');
    parsed.searchParams.set('utm_campaign', surface);
    return parsed.toString();
}
